import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../../../redux/store";
import { ApiError } from "./types/common-types";
import {
  GiftcardProviderState,
  GiftcardProviderResponse,
  GiftcardProvider,
} from "./types/gift-types";
import { PaginationReturn, TextStatusSearchParams } from "./types/types";

const initialState: GiftcardProviderState = {
  giftcardProviders: [],
  allGiftcardProviders: [],
  giftcardProvider: null,
  selectedGiftcardProvider: null,
  loading: true,
  allLoading: false,
  error: null,
  allError: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// CREATE GiftcardProvider
export const createGiftcardProvider = createAsyncThunk<
  GiftcardProviderResponse,
  FormData | Omit<GiftcardProvider, "id" | "created_at" | "updated_at">,
  { rejectValue: ApiError; state: RootState }
>("giftcardProviders/create", async (giftcardProviderData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = giftcardProviderData instanceof FormData;
    const res = await axios.post(
      `${API_URL}/gift-cards-providers`,
      giftcardProviderData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return {
      message: res.data.message,
      giftcardProvider: res.data.data,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create data provider" },
    );
  }
});

// READ (fetch all) GiftcardProviders
export const fetchGiftcardProviders = createAsyncThunk<
  GiftcardProviderResponse & PaginationReturn,
  TextStatusSearchParams,
  { rejectValue: ApiError; state: RootState }
>("giftcardProviders/fetchAll", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const {
      url,
      perPage = 15,
      search,
      status,
      ...queryParams
    } = urlFilters || {};

    const headers = { Authorization: `Bearer ${token}` };

    let res;

    if (url) {
      res = await axios.get(url, { headers });
    } else {
      const params: Record<string, any> = {};

      if (search) params.search = search;
      if (status) params.status = status;
      if (queryParams.page) params.page = queryParams.page;
      if (perPage) params.per_page = perPage;

      res = await axios.get(`${API_URL}/gift-cards-providers`, {
        params,
        headers,
      });
    }
    return {
      message: res.data.data.message,
      giftcardProviders: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch gift card providers" },
    );
  }
});

// READ (fetch all providers without pagination)
export const fetchAllGiftcardProviders = createAsyncThunk<
  GiftcardProvider[],
  void,
  { rejectValue: ApiError; state: RootState }
>("giftcardProviders/fetchAllList", async (_, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const url = new URL(`${API_URL}/gift-cards-providers`);
    url.searchParams.set("per_page", "1000");
    const res = await axios.get(url.toString(), {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payload = res.data?.data;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch gift card providers" },
    );
  }
});

// READ (fetch one) GiftcardProvider by ID
export const fetchGiftcardProviderById = createAsyncThunk<
  GiftcardProvider,
  number,
  { rejectValue: ApiError; state: RootState }
>("giftcardProviders/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/gift-cards-providers/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch gift card provider" },
    );
  }
});

// UPDATE GifcardProvider
export const updateGiftcardProvider = createAsyncThunk<
  GiftcardProvider,
  { id: number; data: FormData | Partial<GiftcardProvider> },
  { rejectValue: ApiError; state: RootState }
>("gitcardProviders/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = data instanceof FormData;
    const res = await axios.post(
      `${API_URL}/gift-cards-providers/${id}?_method=PUT`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update gift card provider" },
    );
  }
});

// DELETE GiftcardProvider
export const deleteGiftcardProvider = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("gitcardProviders/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/gift-cards-providers/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete gift card provider" },
    );
  }
});

// Slice
const giftcardProviderSlice = createSlice({
  name: "giftcardProviders",
  initialState,
  reducers: {
    resetGiftcardProviderState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedGiftcardProvider = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createGiftcardProvider.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createGiftcardProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.giftcardProvider) {
          state.giftcardProviders.unshift(action.payload.giftcardProvider);
        }
      })
      .addCase(createGiftcardProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create gift card provider";
      })

      // Fetch All
      .addCase(fetchGiftcardProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGiftcardProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.giftcardProviders = action.payload.giftcardProviders || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchGiftcardProviders.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch gift card providers";
      })
      .addCase(fetchAllGiftcardProviders.pending, (state) => {
        state.allLoading = true;
        state.allError = null;
      })
      .addCase(fetchAllGiftcardProviders.fulfilled, (state, action) => {
        state.allLoading = false;
        state.allGiftcardProviders = action.payload || [];
      })
      .addCase(fetchAllGiftcardProviders.rejected, (state, action) => {
        state.allLoading = false;
        state.allError =
          action.payload?.message || "Failed to fetch gift card providers";
      })

      // Fetch One
      .addCase(fetchGiftcardProviderById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedGiftcardProvider = null;
      })
      .addCase(fetchGiftcardProviderById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedGiftcardProvider = action.payload;
      })
      .addCase(fetchGiftcardProviderById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch gift card provider";
      })

      // Update
      .addCase(updateGiftcardProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateGiftcardProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.giftcardProviders = state.giftcardProviders.map((provider) =>
          provider.id === action.payload.id ? action.payload : provider,
        );
        if (state.selectedGiftcardProvider?.id === action.payload.id) {
          state.selectedGiftcardProvider = action.payload;
        }
      })
      .addCase(updateGiftcardProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update gift card provider";
      })

      // Delete
      .addCase(deleteGiftcardProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteGiftcardProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.giftcardProviders = state.giftcardProviders.filter(
          (provider) => provider.id !== action.payload,
        );
      })
      .addCase(deleteGiftcardProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete gift card provider";
      });
  },
});

export const { resetGiftcardProviderState } = giftcardProviderSlice.actions;
export default giftcardProviderSlice.reducer;
